import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ScrollView, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import moment from "moment"

import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { ChampionImageM } from "../../../student/champions/ChampionImageM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { liveService } from "@data/services/liveService"
import { authService } from "@data/services/authService"

import { ProxiesSection, PlatformSatisfactionCard, NoStudentCard } from "./SessionCard"
import StudentCloseOutCard, { isBilanFait } from "./StudentCloseOutCard"

const SectionTitle = ({ title, subtitle, counter }) => (
    <View style={{ marginTop: 18, marginBottom: 10 }}>
        <View style={{ flexDirection: "row", alignItems: "baseline" }}>
            <TextM
                fontWeight="Bold"
                fontSize={18}
                color={theme.colors.dark}
            >
                {title}
            </TextM>
            {counter ? (
                <TextM
                    fontWeight="Bold"
                    fontSize={15}
                    color={theme.colors.grey}
                    style={{ marginLeft: 8 }}
                >
                    {counter}
                </TextM>
            ) : null}
        </View>
        {subtitle ? (
            <TextM
                fontSize={12}
                color={theme.colors.grey}
                style={{ marginTop: 2 }}
                wrap
            >
                {subtitle}
            </TextM>
        ) : null}
    </View>
)

const CloseOutScreen = (props) => {
    const { navigation, route } = props
    const occurrenceId = route?.params?.occurrence_id
    const liveSessionId = route?.params?.liveSessionId
    const studyMissionPrescriptions = route?.params?.studyMissionPrescriptions
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()

    const scrollViewRef = useRef(null)
    const cardRefs = useRef({})
    const containerRef = useRef(null)

    const [qualityData, setQualityData] = useState(null)
    const [qualityLoading, setQualityLoading] = useState(true)

    const [liveReports, setLiveReports] = useState([])
    const [qualityScore, setQualityScore] = useState(0)
    const [liveSessionDate, setLiveSessionDate] = useState(null)
    const [isTrial, setIsTrial] = useState(false)
    const [oralScoringEnabled, setOralScoringEnabled] = useState(false)
    const [liveSatisfactionTags, setLiveSatisfactionTags] = useState([])
    const [liveSatisfactionRecord, setLiveSatisfactionRecord] = useState(null)
    const [liveSatisfactionInitialComment, setLiveSatisfactionInitialComment] =
        useState("")

    useEffect(() => {
        if (!occurrenceId) return
        props.apiIdle(liveService.getQualityClosure)
        props.apiRequest(liveService.getQualityClosure, { id: occurrenceId })
        props.apiRequest(liveService.getLiveOccurrence, { id: occurrenceId })
    }, [occurrenceId])

    // Cet écran n'avait aucun re-fetch : le score de productivité orale
    // arrive en asynchrone après la finalisation, donc le tuteur restait
    // indéfiniment sur le score écrit seul — « 0/30 » pour un élève sans
    // activité soumise. Fenêtre bornée : 48 × 5 s = 4 min (deux séances
    // mesurées à 38/55 s puis 113/117 s — 90 s ne suffisaient pas).
    const asyncPollCountRef = useRef(0)
    useEffect(() => {
        if (!occurrenceId || !oralScoringEnabled) return

        const missingScore = liveReports?.some(
            (r) => !!r?.statistics && !r?.statistics?.oral_productivity
        )

        if (!missingScore || asyncPollCountRef.current >= 48) return
        const timer = setTimeout(() => {
            asyncPollCountRef.current += 1
            props.apiRequest(liveService.getLiveOccurrence, { id: occurrenceId })
            // Les étiquettes d'alerte viennent de cet autre endpoint, et son
            // cache est invalidé quand le score atterrit. Sans ce second appel,
            // « Productivité basse » resterait affichée sur l'ancien score
            // écrit seul, même après l'arrivée du composite.
            props.apiRequest(liveService.getQualityClosure, { id: occurrenceId })
        }, 5000)
        return () => clearTimeout(timer)
    }, [liveReports, oralScoringEnabled, occurrenceId])

    useApiStatus(
        liveService.getQualityClosure,
        null,
        false,
        (successData) => {
            setQualityData(successData)
            setQualityLoading(false)
        },
        () => {
            setQualityLoading(false)
        }
    )

    useApiStatus(
        liveService.getLiveOccurrence,
        null,
        false,
        (successData) => {
            setIsTrial(successData?.type === "trial")
            setOralScoringEnabled(!!successData?.oral_scoring_enabled)
            setLiveReports(successData?.live_reports ?? [])
            setQualityScore(successData?.tutor_live_quality_score)
            setLiveSatisfactionInitialComment(successData?.tutor_comment ?? "")
            setLiveSatisfactionTags(successData?.live_satisfaction_tags ?? [])
            setLiveSatisfactionRecord(successData?.live_satisfaction_record ?? null)
            setLiveSessionDate(
                successData?.date
                    ? moment(successData.date)
                          .format("dddd DD MMMM HH:mm")
                          .toUpperCase()
                    : null
            )
        },
        () => {}
    )

    const reportsNeedingBilan = useMemo(
        () => liveReports.filter((r) => !!r?.statistics),
        [liveReports]
    )

    const [bilanFaitByReportId, setBilanFaitByReportId] = useState({})

    const handleBilanFaitChange = useCallback((reportId, value) => {
        setBilanFaitByReportId((prev) => {
            if (prev[reportId] === value) return prev
            return { ...prev, [reportId]: value }
        })
    }, [])

    const effectiveBilanFait = useCallback(
        (report) => {
            if (bilanFaitByReportId[report.id] !== undefined) {
                return bilanFaitByReportId[report.id]
            }
            return isBilanFait(report)
        },
        [bilanFaitByReportId]
    )

    const completedCount = useMemo(
        () =>
            reportsNeedingBilan.filter((r) => effectiveBilanFait(r)).length,
        [reportsNeedingBilan, effectiveBilanFait]
    )

    const remaining = reportsNeedingBilan.length - completedCount
    const allDone =
        reportsNeedingBilan.length > 0 && remaining === 0

    // Nobody attended: the backend already dropped the rules that measure the
    // tutor against present students. What is left of "Les bases" is only worth
    // showing when it still has something to say — an unstable connection or a
    // phone stays true whoever turned up. All-green axes on a session nobody
    // attended would just be a verdict on nothing.
    const noStudentAttended = !!qualityData?.no_student_attended
    const proxiesToShow = useMemo(() => {
        const proxies = qualityData?.tutor_proxies ?? []
        if (!qualityData?.no_student_attended) return proxies
        return proxies.filter((proxy) => (proxy?.alerts?.length ?? 0) > 0)
    }, [qualityData])

    const qualityStudentById = useMemo(() => {
        const map = {}
        ;(qualityData?.students ?? []).forEach((s) => {
            map[s.student_id] = s
        })
        return map
    }, [qualityData])

    const containerWidth =
        dimensions?.width > 600
            ? 560
            : dimensions?.width
              ? dimensions.width - 32
              : "100%"

    const scrollToNextPending = useCallback(
        (afterReportId) => {
            const idx = liveReports.findIndex((r) => r.id === afterReportId)
            if (idx < 0) return
            const next = liveReports.slice(idx + 1).find(
                (r) => !!r?.statistics && !effectiveBilanFait(r)
            )
            if (!next) return
            const nextRef = cardRefs.current[next.id]
            if (!nextRef?.measureInWindow) return
            nextRef.measureInWindow((x, y) => {
                const targetTopFromWindow = insets.top + 80
                const delta = y - targetTopFromWindow
                if (Math.abs(delta) < 4) return
                scrollViewRef.current?.scrollTo({
                    y: Math.max(
                        0,
                        (scrollViewRef.current.contentOffset?.y ?? 0) + delta
                    ),
                    animated: true,
                })
            })
        },
        [liveReports, insets.top, effectiveBilanFait]
    )

    const handleCardDone = useCallback(
        (reportId) => {
            setTimeout(() => scrollToNextPending(reportId), 150)
        },
        [scrollToNextPending]
    )

    // Both exits leave the live, if the tutor is still in it. The closure
    // already left it (the reports are recorded outside the live), so this
    // only catches a leave that failed: going back without leaving would drop
    // the tutor into the room they just closed. Opened from home, there is no
    // liveSessionId and nothing to leave.
    const leaveLiveAndGoBack = () => {
        if (liveSessionId && props.isLiveModeEnabled) {
            props.apiRequest(liveService.leaveLiveSession, { id: liveSessionId })
        }
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }

    const handleTerminer = () => {
        leaveLiveAndGoBack()
        props.apiRequest(authService.getUser)
    }

    // "Plus tard": leaves with bilans still pending. Autosaves are already
    // persisted server-side per field. Tutor can resume from home.
    const handlePlusTard = () => {
        DialogM.show({
            champion: "zoe",
            variant: "main",
            text1: "Tu n'as pas tout complété",
            text2:
                `Il te reste ${remaining} compte-rendu${remaining > 1 ? "s" : ""} à finaliser. ` +
                "Les parents recevront un rapport incomplet si la session est finalisée maintenant. " +
                "Tu peux y revenir depuis l'accueil.",
            buttonText1: "Revenir",
            onPress: () => {},
            buttonText3: "Continuer plus tard",
            onPress3: leaveLiveAndGoBack,
            exitButton: true,
            onPressBack: () => {},
        })
    }

    if (qualityLoading) {
        return (
            <View
                style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: theme.colors.greyBg,
                }}
            >
                <ActivityIndicator
                    animating
                    color={theme.colors.primary}
                    size="large"
                />
            </View>
        )
    }

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.greyBg,
                paddingTop: insets.top,
            }}
        >
            <ScrollView
                ref={scrollViewRef}
                style={{ flex: 1 }}
                contentContainerStyle={{
                    alignItems: "center",
                    paddingBottom: 100,
                    paddingHorizontal: 16,
                }}
                onScroll={(e) => {
                    if (scrollViewRef.current) {
                        scrollViewRef.current.contentOffset = {
                            y: e.nativeEvent.contentOffset.y,
                            x: e.nativeEvent.contentOffset.x,
                        }
                    }
                }}
                scrollEventThrottle={32}
            >
                <View ref={containerRef} style={{ width: containerWidth }}>
                    <View
                        style={{
                            alignItems: "center",
                            paddingTop: 10,
                            paddingBottom: 4,
                        }}
                    >
                        <ChampionImageM name="zoe" variant="main" width={86} />
                        <TextM
                            fontWeight="Bold"
                            fontSize={22}
                            color={theme.colors.primary}
                            center
                            style={{ marginTop: 8 }}
                            wrap
                        >
                            Bilan de la séance
                        </TextM>
                        {liveSessionDate ? (
                            <TextM
                                fontSize={12}
                                color={theme.colors.light}
                                center
                                style={{ marginTop: 4 }}
                            >
                                Live du {liveSessionDate}
                            </TextM>
                        ) : null}
                    </View>

                    <SectionTitle title="Les bases" />
                    {noStudentAttended ? (
                        <NoStudentCard
                            tutorConnectionMinutes={
                                qualityData?.tutor_connection_minutes
                            }
                        />
                    ) : null}
                    <ProxiesSection tutorProxies={proxiesToShow} />

                    <SectionTitle
                        title="Tes élèves"
                        counter={
                            reportsNeedingBilan.length > 0
                                ? `(${completedCount}/${reportsNeedingBilan.length})`
                                : null
                        }
                    />

                    {liveReports.length === 0 ? (
                        <TextM
                            fontSize={14}
                            color={theme.colors.light}
                            center
                            style={{ marginVertical: 20 }}
                        >
                            Aucun élève sur ce live.
                        </TextM>
                    ) : (
                        liveReports.map((report) => {
                            const sid = report?.student?.id
                            return (
                                <StudentCloseOutCard
                                    ref={(el) => {
                                        if (el) cardRefs.current[report.id] = el
                                        else delete cardRefs.current[report.id]
                                    }}
                                    key={report.id}
                                    liveReport={report}
                                    qualityStudent={qualityStudentById[sid]}
                                    isTrial={isTrial}
                                    oralScoringEnabled={oralScoringEnabled}
                                    missionPrescription={
                                        studyMissionPrescriptions?.[sid]
                                    }
                                    onBilanFaitChange={handleBilanFaitChange}
                                    onDone={handleCardDone}
                                />
                            )
                        })
                    )}

                    <SectionTitle title="Côté plateforme" />
                    <PlatformSatisfactionCard
                        occurrenceId={occurrenceId}
                        qualityScore={qualityScore}
                        setQualityScore={setQualityScore}
                        liveSatisfactionTags={liveSatisfactionTags}
                        liveSatisfactionRecord={liveSatisfactionRecord}
                        liveSatisfactionInitialComment={
                            liveSatisfactionInitialComment
                        }
                    />
                </View>
            </ScrollView>

            <View
                style={{
                    backgroundColor: theme.colors.white,
                    paddingTop: 10,
                    paddingBottom: Math.max(insets.bottom, 10),
                    paddingHorizontal: 16,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                    alignItems: "center",
                }}
            >
                {allDone || reportsNeedingBilan.length === 0 ? (
                    <FilledButtonM
                        label="Terminer la session"
                        icon="check"
                        onPress={handleTerminer}
                        color={theme.colors.success}
                        style={{ width: containerWidth }}
                    />
                ) : (
                    <View style={{ alignItems: "center", width: containerWidth }}>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            center
                            style={{ marginBottom: 4 }}
                            wrap
                        >
                            Il te reste {remaining} compte-rendu
                            {remaining > 1 ? "s" : ""}
                        </TextM>
                        <FilledButtonM
                            label="Plus tard"
                            mode="text"
                            color={theme.colors.grey}
                            onPress={handlePlusTard}
                            style={{
                                width: null,
                                marginBottom: 0,
                            }}
                            contentStyle={{ height: 36 }}
                        />
                    </View>
                )}
            </View>
        </View>
    )
}

const mapStateToProps = (state) => ({
    user: state.data.currentUser?.object,
    isLiveModeEnabled: state.liveMode?.isLiveModeEnabled,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(CloseOutScreen)
